import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createActiveStepError,
  createMessage,
  IWestNegevLoginLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
  WestNegevLoginStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { useYupLoginLandingWestNegevValidationSchema } from "./parts";

export const useStepLoginWestNegev = () => {
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { yupLoginLandingWestNegevValidationSchema } =
    useYupLoginLandingWestNegevValidationSchema();

  const resolver = useYupValidationResolver(
    yupLoginLandingWestNegevValidationSchema,
    "landing",
  );

  const methods = useForm<IWestNegevLoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      phoneMobile: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const phoneMobileValue = watch(WestNegevLoginStepFormFieldsEnum.PhoneMobile);

  const isContinueCanBeApply = isValid && !!phoneMobileValue;

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const phoneMobile = getValues(WestNegevLoginStepFormFieldsEnum.PhoneMobile);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestPublicOtp({
        allowExistingPassenger: true,
        recaptchaToken,
        phone: phoneMobile,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          phone: phoneMobile,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP via SMS", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleLogin, isContinueCanBeApply };
};
